// Billing approval (#1621): an order pauses after its Autotask ticket until Billing ops
// or a Super admin choose the OneBill account it bills to. This file holds the account decision as
// the lists show it, and the approval sheet from the M4 mock
// (docs/plans/lens-dealer-portal/billing-approval.html): the account, the lines to
// bill, the Autotask change, then approve, and the run that follows.
import React, { useEffect, useRef, useState } from "react";
import {
  accountNumberSource,
  formatCents,
  lineValueProblem,
  normalizeLineValue,
  telephoneDigits,
  TELEPHONE_NUMBER_FEATURE,
  type AwaitingBillingApprovalOrder,
  type BillingApprovalRequest,
  type BillingLineFeature,
  type BillingPrefillSource,
  type BillingLineRequirement,
  type BillingLineValue,
  type BillingRequirements,
  type CustomerBilling,
  type CustomerContact,
  type CustomerSummary,
  type CustomerV2,
  type Dealer,
  type Order,
  type Quote,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { categoryLinkOf, companyOf, companyTypeName, isRppNumber, onebillHasNumber, type OneBillAccountMatch } from "../api-customers.js";
import { Icon, type IconName } from "../icons.js";
import { Link } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { errorText, phoneWords, plural, whenIn, type ChipKind } from "../format.js";
import { Callout, Chip, Sheet } from "../ui.js";
import { useRepNames } from "./Quotes.js";
import { announceBillingChange } from "../billing-changes.js";
import { AccountNumber } from "./customer-bits.js";

// ------------------------------------------------------------------ what Autotask gives OneBill

export type Missing = "address" | "email";

/** What OneBill would be given to open an account, read the way the server reads it. */
export interface FromAutotask {
  missing: Missing[];
  address: string | null;
  contactName: string | null;
  email: string | null;
  /** The email is the signer's from the quote, because no Autotask contact has one. */
  emailFromQuote: boolean;
  phone: string | null;
}

function emailOf(c: CustomerContact): string {
  return c.email?.trim() ?? "";
}

/**
 * The server's create rule (routes/orders.ts): an address with street, city, state and ZIP, and an email from
 * the billing contact, else the primary contact, else any contact, else the signer on the quote.
 */
export function fromAutotask(customer: CustomerV2, quote: Quote | null): FromAutotask {
  const a = customer.address;
  const complete = Boolean(a && a.line1?.trim() && a.city?.trim() && a.state?.trim() && a.postalCode?.trim());
  const contacts = customer.contacts;
  const withEmail =
    contacts.find((c) => c.roles.includes("billing") && emailOf(c)) ?? contacts.find((c) => c.roles.includes("primary") && emailOf(c)) ?? contacts.find((c) => emailOf(c));
  const shown = withEmail ?? contacts.find((c) => c.roles.includes("billing")) ?? contacts.find((c) => c.roles.includes("primary")) ?? contacts[0] ?? null;
  const quoteEmail = quote?.contactEmail?.trim() || null;
  const email = (withEmail ? emailOf(withEmail) : "") || quoteEmail;
  const missing: Missing[] = [];
  if (!complete) missing.push("address");
  if (!email) missing.push("email");
  return {
    missing,
    address: a ? [a.line1, a.line2, `${a.city}, ${a.state} ${a.postalCode}`.trim()].filter((part) => part && part.replace(/[ ,]/g, "")).join(", ") : null,
    contactName: shown?.name ?? quote?.contactName ?? null,
    email,
    emailFromQuote: !withEmail && Boolean(quoteEmail),
    phone: shown?.phone?.trim() || customer.phone,
  };
}

// ------------------------------------------------------------------ the account decision

export interface Decision {
  kind: ChipKind;
  icon: IconName;
  label: string;
  text: string;
  state: "linked" | "pick" | "blocked" | "create" | "reading";
}

/** The account decision as a chip and a few words. `missing` is null while Autotask is being read. */
export function decisionOf(billing: CustomerBilling, missing: Missing[] | null, unreadable = false): Decision {
  if (billing.state === "linked") return { kind: "ok", icon: "link", label: "Account linked", text: billing.accountNumber, state: "linked" };
  if (billing.state === "unreviewed") {
    return { kind: "warn", icon: "search", label: "Pick an account", text: `${plural(billing.candidates.length, "suggestion")} from OneBill`, state: "pick" };
  }
  if (unreadable) return { kind: "", icon: "plus", label: "New account", text: "Autotask didn't answer", state: "reading" };
  if (missing === null) return { kind: "", icon: "plus", label: "New account", text: "Reading Autotask", state: "reading" };
  if (missing.length) return { kind: "stop", icon: "lock", label: "Blocked", text: `No ${missing.join(" or ")} in Autotask`, state: "blocked" };
  return { kind: "", icon: "plus", label: "New account", text: "From the Autotask record", state: "create" };
}

/** The decision for one order to bill. Only a customer with no OneBill account needs Autotask read to know whether it can be created. */
export function useDecision(o: AwaitingBillingApprovalOrder): Decision {
  const { api } = useSession();
  const none = o.customerBilling.state === "none";
  const read = useLoad<Missing[] | null>(
    () => (none ? Promise.all([api.customer(o.customerId), api.quote(o.quoteId)]).then(([c, q]) => fromAutotask(c, q).missing) : Promise.resolve([])),
    [o.id, o.customerId, none],
  );
  return decisionOf(o.customerBilling, read.data, read.error !== null && read.data === null);
}

export function DecisionChip({ decision, testId }: { decision: Decision; testId?: string }): React.ReactElement {
  return (
    <Chip kind={decision.kind} icon={decision.icon} testId={testId} status={decision.state}>
      {decision.label}
    </Chip>
  );
}

/** "Prospect, becomes Customer" or "Already a Customer", from Autotask's company type. */
export function typeWords(companyType: number | null | undefined): string {
  if (companyType === 3) return "Prospect, becomes Customer";
  if (companyType === 1) return "Already a Customer";
  if (companyType === undefined || companyType === null) return "";
  return `${companyTypeName(companyType)} in Autotask`;
}

// ------------------------------------------------------------------ the sheet

type Choice = { kind: "existing"; accountNumber: string; name: string | null; status: string | null } | { kind: "create" } | null;

interface Refused {
  accountNumber: string;
  name: boolean;
  accountName: string | null;
  shared: boolean;
}

const LINKED_VIA: Record<Extract<CustomerBilling, { state: "linked" }>["via"], string> = {
  billing_map: "the billing map",
  name_exact: "an exact name match",
  name_near_exact: "a near-exact name match",
  ops: "Phoneware",
  order: "its first order",
  account_number: "an account number match",
};

/** A refused approval in plain words, and what the refusal says needs ticking. */
function refusal(err: unknown, customerName: string, accountNumber: string | null): { text: string; refused: Refused | null; stale: boolean; reread: boolean } {
  if (!(err instanceof ApiError)) return { text: errorText(err), refused: null, stale: false, reread: false };
  if (err.status === 409) {
    return { text: "Someone changed this order since you opened it, so Omni read it again. Check the account and approve again.", refused: null, stale: true, reread: false };
  }
  if (err.status === 422) {
    const body = err.body && typeof err.body === "object" ? (err.body as { missingFields?: unknown }) : null;
    const fields = Array.isArray(body?.missingFields) ? body.missingFields.filter((f): f is string => typeof f === "string") : [];
    if (fields.length) {
      const words = fields.map((f) => (f === "email" ? "an email" : f === "address" ? "an address" : f)).join(" and ");
      return {
        text: `OneBill can't open an account without ${words} from Autotask, and Autotask has none for ${customerName}. Fix it in Autotask and read it again, or bill an account OneBill already has.`,
        refused: null,
        stale: false,
        reread: true,
      };
    }
    const differs = /Account name "(.*)" differs/.exec(err.message);
    if (differs && accountNumber) {
      return {
        text: `OneBill has ${accountNumber} under the name ${differs[1]}. Check it is the same business as ${customerName}, tick the box, then approve again.`,
        refused: { accountNumber, name: true, accountName: differs[1], shared: false },
        stale: false,
        reread: false,
      };
    }
    if (/belongs to another customer/.test(err.message) && accountNumber) {
      return {
        text: `${accountNumber} already bills another customer. Tick the box to bill ${customerName} on it too, then approve again.`,
        refused: { accountNumber, name: false, accountName: null, shared: true },
        stale: false,
        reread: false,
      };
    }
  }
  return { text: err.message, refused: null, stale: false, reread: false };
}

function Opt({
  checked,
  disabled,
  title,
  meta,
  tags,
  onPick,
  testId,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  title: React.ReactNode;
  meta: React.ReactNode;
  tags?: React.ReactNode[];
  onPick: () => void;
  testId?: string;
  children?: React.ReactNode;
}): React.ReactElement {
  const pick = () => {
    if (!disabled) onPick();
  };
  return (
    <div
      className="opt"
      role="radio"
      tabIndex={disabled ? -1 : 0}
      aria-checked={checked}
      aria-disabled={disabled ? true : undefined}
      data-testid={testId}
      onClick={pick}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          pick();
        }
      }}
    >
      <span className="radio" />
      <div className="opt-main">
        <b>{title}</b>
        <span className="row-m">{meta}</span>
        {tags && tags.length ? <div className="opt-tags">{tags}</div> : null}
        {children}
      </div>
    </div>
  );
}

function FromAutotaskList({ customer, from }: { customer: CustomerV2; from: FromAutotask }): React.ReactElement {
  const gap = (text: string) => (
    <dd className="gap">
      <Icon name="triangle-alert" size={12} />
      {text}
    </dd>
  );
  return (
    <dl className="from-at" data-testid="ab-from-autotask">
      <dt>Account number</dt>
      <dd>
        {customer.accountNumber?.trim() ? (
          <>
            <AccountNumber number={customer.accountNumber} /> <span className="muted">(from Autotask)</span>
          </>
        ) : (
          <span className="muted">The next PW number, written to Autotask first</span>
        )}
      </dd>
      <dt>Account name</dt>
      <dd>{customer.name}</dd>
      <dt>Address</dt>
      {from.missing.includes("address") ? gap(from.address ? `Incomplete in Autotask: ${from.address}` : "No address in Autotask") : <dd>{from.address}</dd>}
      <dt>Billing contact</dt>
      <dd>{from.contactName ?? <span className="muted">No contact in Autotask</span>}</dd>
      <dt>Email</dt>
      {from.email ? (
        <dd>
          {from.email}
          {from.emailFromQuote ? <span className="muted"> · from the signed quote</span> : null}
        </dd>
      ) : (
        gap("No email on any contact in Autotask")
      )}
      <dt>Phone</dt>
      <dd>{from.phone ?? <span className="muted">None in Autotask</span>}</dd>
    </dl>
  );
}

// ------------------------------------------------------------------ what each line's OneBill plan needs (#1732)

/** The key a line's feature value is kept under. */
const featureKey = (line: number, featureName: string): string => `${line}:${featureName}`;
const featureSlug = (featureName: string): string => featureName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
/** What a field starts with: a Telephone Number written the way people read it, anything else as given. */
const startingValue = (f: BillingLineFeature): string =>
  f.featureName === TELEPHONE_NUMBER_FEATURE && telephoneDigits(f.defaultValue) ? phoneWords(f.defaultValue) : f.defaultValue;

/** The line and feature a 400 from the approval names, when it names one. */
function namedRefusal(err: unknown): { line: number; featureName: string } | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  if (!body || typeof body !== "object" || !("line" in body) || !("featureName" in body)) return null;
  return typeof body.line === "number" && typeof body.featureName === "string" ? { line: body.line, featureName: body.featureName } : null;
}

/** Where a Telephone Number started (#1763), in the words the tag beside it shows while it still holds that number. */
const PREFILLED_FROM: Record<BillingPrefillSource, string> = { seller: "From the seller", main_phone: "The customer\u2019s main number" };

/** The note under a field: what OneBill wants, and where the Telephone Number started. A number never breaks across lines. */
function featureNote(f: BillingLineFeature, value: string): React.ReactNode {
  if (f.featureName !== TELEPHONE_NUMBER_FEATURE) return "OneBill's plan for this line requires it.";
  if (!f.defaultValue) return "OneBill's plan for this line requires it. The seller gave none, and Autotask has no main number to start from.";
  const kept = telephoneDigits(value) === f.defaultValue;
  if (kept) {
    return f.prefilledFrom === "seller"
      ? "OneBill's plan for this line requires it. The seller entered this number on the order."
      : "OneBill's plan for this line requires it. This is the main number in Autotask.";
  }
  return (
    <>
      {f.prefilledFrom === "seller" ? "OneBill's plan for this line requires it. The seller entered " : "OneBill's plan for this line requires it. The main number in Autotask is "}
      <span className="ab-num">{phoneWords(f.defaultValue)}</span>.
    </>
  );
}

export function BillingApprovalSheet({ orderId, onClose, onChanged }: { orderId: string; onClose: () => void; onChanged?: (o: Order) => void }): React.ReactElement {
  const { api } = useSession();
  const repName = useRepNames();
  const order = useLoad<Order>(() => api.order(orderId), [orderId]);
  const o = order.data;
  const quote = useLoad<Quote | null>(() => (o ? api.quote(o.quoteId) : Promise.resolve(null)), [o?.quoteId]);
  const customer = useLoad<CustomerV2 | null>(() => (o ? api.customer(o.customerId) : Promise.resolve(null)), [o?.customerId]);
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const c = customer.data;
  const linkedNumber = c?.billing.state === "linked" ? c.billing.accountNumber : null;
  const linked = useLoad<OneBillAccountMatch | null>(
    () => (linkedNumber ? api.onebillAccounts(linkedNumber).then((list) => list.find((a) => a.accountNumber === linkedNumber) ?? null) : Promise.resolve(null)),
    [linkedNumber],
  );
  // What each line's OneBill plan needs entered, read only while ops decide.
  const waiting = o?.status === "awaiting_billing_approval";
  const requirements = useLoad<BillingRequirements | null>(() => (o && waiting ? api.billingRequirements(o.id) : Promise.resolve(null)), [o?.id, waiting]);

  const [choice, setChoice] = useState<Choice>(null);
  const [picked, setPicked] = useState(false);
  const [ackName, setAckName] = useState(false);
  const [ackShared, setAckShared] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ query: string; list: OneBillAccountMatch[]; error: string | null } | null>(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [refused, setRefused] = useState<Refused | null>(null);
  const [reread, setReread] = useState<string | null>(null);
  // The account number: swapping in an RPP number, and OneBill refusing one it already has.
  const [rppOpen, setRppOpen] = useState(false);
  const [rppVal, setRppVal] = useState("");
  const [rppErr, setRppErr] = useState<string | null>(null);
  const [rppSaving, setRppSaving] = useState(false);
  const [replaced, setReplaced] = useState<string | null>(null);
  const [taken, setTaken] = useState<{ accountNumber: string; name: string | null; status: string | null } | null>(null);
  // The values ops enter for the lines' OneBill plans: typed, left (so its problem shows), and what the server refused.
  const [values, setValues] = useState<Record<string, string>>({});
  const [left, setLeft] = useState<Record<string, boolean>>({});
  const [refusedValues, setRefusedValues] = useState<Record<string, string>>({});
  const problemRef = useRef<HTMLDivElement>(null);

  const q = quote.data;
  // OneBill gets the quote's email when Autotask has none, so the decision waits for the quote too.
  const from = c && (q || quote.error) ? fromAutotask(c, q) : null;
  const ready = Boolean(o && c && q && from);

  // The default choice: the linked account, or a new one when Autotask has what OneBill needs. Never over a pick.
  useEffect(() => {
    if (picked || !c || !from) return;
    if (c.billing.state === "linked") {
      setChoice({ kind: "existing", accountNumber: c.billing.accountNumber, name: linked.data?.name ?? null, status: linked.data?.status ?? null });
    } else if (c.billing.state === "none" && from.missing.length === 0) {
      setChoice({ kind: "create" });
    } else {
      setChoice(null);
    }
  }, [picked, c, q, linked.data]);

  // Each field starts with what the server proposes; a value ops already typed stays when the lines are read again.
  useEffect(() => {
    const lines = requirements.data?.lines;
    if (!lines) return;
    setValues((now) => {
      const next = { ...now };
      for (const l of lines) for (const f of l.features) next[featureKey(l.line, f.featureName)] ??= startingValue(f);
      return next;
    });
  }, [requirements.data]);

  // OneBill search, a beat after typing stops.
  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) {
      setResults(null);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      api.onebillAccounts(text).then(
        (list) => {
          if (live) setResults({ query: text, list, error: null });
        },
        (err: unknown) => {
          if (live) setResults({ query: text, list: [], error: errorText(err) });
        },
      );
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const deciding = o?.status === "awaiting_billing_approval" && !sent;
  const running = o?.status === "in_progress";
  useEffect(() => {
    if (deciding || !running) return;
    const timer = window.setInterval(() => order.reload(), 1500);
    return () => window.clearInterval(timer);
  }, [deciding, running]);

  useEffect(() => {
    if (problem) problemRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [problem]);

  if (!o) {
    return (
      <Sheet title="Approve billing" onClose={onClose} wide>
        {order.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {order.error.message}
          </Callout>
        ) : (
          <div className="empty">Loading the order.</div>
        )}
      </Sheet>
    );
  }

  const dealer = dealers.data?.find((d) => d.id === o.dealerId) ?? null;
  const category = dealer ? categoryLinkOf(dealer) : null;
  const lines = q?.lines ?? [];
  const totals = lines.reduce(
    (t, l) => (l.billing === "monthly" ? { ...t, monthly: t.monthly + l.qty * l.unitPrice } : { ...t, oneTime: t.oneTime + l.qty * l.unitPrice }),
    { monthly: 0, oneTime: 0 },
  );
  const companyType = c?.companyType ?? null;
  const prospect = companyType === 3;
  const billing = c?.billing ?? null;
  const blocked = billing?.state === "none" && Boolean(from?.missing.length);
  const canCreate = billing !== null && billing.state !== "linked" && from !== null && from.missing.length === 0;

  const pickAccount = (a: { accountNumber: string; name: string | null; status: string | null }) => {
    setPicked(true);
    setChoice({ kind: "existing", ...a });
    setAckName(false);
    setAckShared(false);
    setProblem(null);
  };
  const pickCreate = () => {
    setPicked(true);
    setChoice({ kind: "create" });
    setProblem(null);
  };

  const owner = (accountNumber: string) =>
    customers.data?.find((x) => x.billing.state === "linked" && x.billing.accountNumber === accountNumber && x.autotaskCompanyId !== o.customerId) ?? null;
  const existing = choice?.kind === "existing" ? choice : null;
  const sharedWith = existing ? owner(existing.accountNumber) : null;
  const refusedHere = existing && refused?.accountNumber === existing.accountNumber ? refused : null;
  const accountName = existing ? (existing.name ?? refusedHere?.accountName ?? null) : null;
  // The server compares names lowercased with everything but letters and digits removed (customers/normalize.ts).
  const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const needsName = Boolean(existing && ((accountName !== null && squash(accountName) !== squash(o.customerName)) || refusedHere?.name));
  const needsShared = Boolean(existing && (sharedWith || refusedHere?.shared));
  const inactive = Boolean(existing?.status && existing.status !== "Active");
  const number = c?.accountNumber?.trim() || null;
  const numberSource = number ? accountNumberSource(number) : null;
  // OneBill refused to create this number: the create choice stays refused until the number changes.
  const takenHere = taken !== null && taken.accountNumber === number ? taken : null;
  const decided = rppOpen
    ? false
    : choice?.kind === "create"
      ? canCreate && takenHere === null
      : existing
        ? !inactive && (!needsName || ackName) && (!needsShared || ackShared)
        : false;

  const n = lines.length;
  const tail =
    companyType === 3
      ? ", then makes the company a Customer in Autotask."
      : companyType === 1
        ? ". Autotask already has a Customer, so nothing changes there."
        : companyType === null
          ? "."
          : `. Autotask lists it as a ${companyTypeName(companyType)}, and Omni changes only a Prospect.`;
  let why: { text: string; stop: boolean };
  if (rppOpen) why = { text: "Use the RPP number or keep the one Autotask has first.", stop: true };
  else if (choice?.kind === "create" && takenHere) why = { text: `OneBill already has account ${takenHere.accountNumber}. Link it instead.`, stop: true };
  else if (choice?.kind === "create") why = { text: `Approving creates OneBill account ${number ?? "with the next PW number"}, places ${plural(n, "line")}${tail}`, stop: false };
  else if (existing && inactive) why = { text: `${existing.accountNumber} is ${existing.status} in OneBill. Choose an active account.`, stop: true };
  else if (existing && !decided) why = { text: "Check the box above first.", stop: true };
  else if (existing) why = { text: `Approving places ${plural(n, "line")} on ${existing.accountNumber}${tail}`, stop: false };
  else if (blocked && from) why = { text: `Add the ${from.missing.join(" and ")} in Autotask first, or pick an account OneBill already has.`, stop: true };
  else why = { text: "Choose a OneBill account first.", stop: true };

  // The lines' OneBill values: read, every one good, then the account sentence above gives way to what is missing.
  const reqLines: BillingLineRequirement[] = requirements.error ? [] : (requirements.data?.lines ?? []);
  const linesRead = !requirements.error && requirements.data !== null;
  const valueOf = (l: BillingLineRequirement, f: BillingLineFeature): string => values[featureKey(l.line, f.featureName)] ?? startingValue(f);
  const firstGap = reqLines.flatMap((l) => l.features.map((f) => ({ l, f, problem: lineValueProblem(f.featureName, valueOf(l, f)) }))).find((x) => x.problem !== null) ?? null;
  const linesOk = linesRead && firstGap === null;
  if (!why.stop && requirements.error) why = { text: "Read OneBill again first: Omni needs to know what these lines need.", stop: true };
  else if (!why.stop && firstGap) {
    why = valueOf(firstGap.l, firstGap.f).trim()
      ? { text: `The ${firstGap.f.featureName} for ${firstGap.l.name} is 10 digits.`, stop: true }
      : { text: `Enter the ${firstGap.f.featureName} for ${firstGap.l.name} first.`, stop: true };
  }

  async function approve(): Promise<void> {
    if (!o || !decided || !choice || !linesOk) return;
    const trimmed = note.trim() || null;
    const lineValues: BillingLineValue[] = reqLines.flatMap((l) =>
      l.features.map((f) => ({ line: l.line, featureName: f.featureName, value: normalizeLineValue(f.featureName, valueOf(l, f)) })),
    );
    const withLines = lineValues.length > 0 ? { lineValues } : {};
    const body: BillingApprovalRequest =
      choice.kind === "create"
        ? { kind: "create", expectedUpdatedAt: o.updatedAt, note: trimmed, ...withLines }
        : {
          kind: "existing",
          accountNumber: choice.accountNumber,
          acknowledgeNameMismatch: needsName,
          acknowledgeSharedAccount: needsShared,
          expectedUpdatedAt: o.updatedAt,
          note: trimmed,
          ...withLines,
        };
    setSending(true);
    setProblem(null);
    setRefusedValues({});
    try {
      const next = await api.approveBilling(o.id, body);
      order.set(next);
      setSent(true);
      onChanged?.(next);
      announceBillingChange();
    } catch (err) {
      const has = choice.kind === "create" ? onebillHasNumber(err) : null;
      if (has) {
        refuseTaken(has);
        return;
      }
      // A value the server refused shows under its field. The lines are read again, so a feature OneBill added since
      // the sheet opened gets its field too, empty, so the refusal and the field agree that it needs a value.
      const named = namedRefusal(err);
      if (named && err instanceof ApiError) {
        const k = featureKey(named.line, named.featureName);
        setValues((v) => (k in v ? v : { ...v, [k]: "" }));
        setRefusedValues({ [k]: err.message });
        requirements.reload();
        return;
      }
      const r = refusal(err, o.customerName, existing?.accountNumber ?? null);
      setProblem(r.text);
      if (r.refused) setRefused(r.refused);
      if (r.stale) {
        order.reload();
        customer.reload();
        customers.reload();
      }
      if (r.reread) customer.reload();
    } finally {
      setSending(false);
    }
  }

  /** OneBill already has the number Omni would create: say so, read the account, and offer to link it. Nothing was created. */
  function refuseTaken(accountNumber: string): void {
    setTaken({ accountNumber, name: null, status: null });
    setProblem(null);
    api.onebillAccounts(accountNumber).then(
      (list) => {
        const found = list.find((a) => a.accountNumber === accountNumber);
        if (found) setTaken((t) => (t && t.accountNumber === accountNumber ? { accountNumber, name: found.name, status: found.status } : t));
      },
      () => undefined,
    );
  }

  function openRpp(): void {
    setRppOpen(true);
    setRppVal(numberSource === "rpp" && number ? number : "");
    setRppErr(null);
  }

  /** PUT /api/customers/:id/account-number: the RPP number replaces the one Autotask has, while there is no linked OneBill account. */
  async function saveRpp(): Promise<void> {
    if (!c) return;
    const value = rppVal.trim();
    if (!isRppNumber(value)) {
      setRppErr("An RPP number is 4 to 6 digits.");
      return;
    }
    if (value === number) {
      setRppErr(`${value} is already the number.`);
      return;
    }
    const expectedUpdatedAt = c.updatedAt ?? null;
    if (!expectedUpdatedAt) {
      setRppErr("Omni couldn't tell when this customer last changed. Close the sheet and open it again.");
      return;
    }
    setRppSaving(true);
    setRppErr(null);
    try {
      const next = await api.replaceAccountNumber(c.autotaskCompanyId, { number: value, expectedUpdatedAt });
      if (number) setReplaced(number);
      customer.set(next);
      setRppOpen(false);
      setPicked(false);
      customers.reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && /linked/i.test(err.message)) {
        // Linked now: the number can't change any more, so the swap closes and the sheet says why.
        setRppOpen(false);
        setProblem(`${c.name} has a linked OneBill account now, so its number stays. Omni read the customer again.`);
        customer.reload();
      } else if (err instanceof ApiError && err.status === 409) {
        setRppErr("Someone changed this customer since you opened it, so Omni read it again. Check the number and try again.");
        customer.reload();
      } else if (err instanceof ApiError && err.status === 400) {
        setRppErr("An RPP number is 4 to 6 digits.");
      } else {
        setRppErr(errorText(err));
      }
    } finally {
      setRppSaving(false);
    }
  }

  async function retry(): Promise<void> {
    if (!o) return;
    setRetrying(true);
    setProblem(null);
    try {
      const next = await api.retryOrder(o.id);
      order.set(next);
      onChanged?.(next);
      announceBillingChange();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setRetrying(false);
    }
  }

  async function readAgain(): Promise<void> {
    if (!o) return;
    const before = from?.missing ?? [];
    try {
      const fresh = await api.customer(o.customerId);
      customer.set(fresh);
      const after = fromAutotask(fresh, q).missing;
      setReread(
        after.length === 0
          ? `Read Autotask again: ${fresh.name} now has an address and an email.`
          : `Read Autotask again: still no ${after.join(" or ")}${before.length !== after.length ? ", the rest is there now" : ""}.`,
      );
    } catch (err) {
      setReread(`Autotask didn't answer: ${errorText(err)}`);
    }
  }

  const ackBoxes = existing ? (
    <>
      {needsName ? (
        <label className={ackName ? "ack done" : "ack"}>
          <input type="checkbox" data-testid="ab-ack-name" checked={ackName} onChange={(e) => setAckName(e.target.checked)} />
          <span>
            I checked that <b>{accountName ?? existing.accountNumber}</b> in OneBill is the same business as <b>{o.customerName}</b>.
          </span>
        </label>
      ) : null}
      {needsShared ? (
        <label className={ackShared ? "ack done" : "ack"}>
          <input type="checkbox" data-testid="ab-ack-shared" checked={ackShared} onChange={(e) => setAckShared(e.target.checked)} />
          <span>
            {existing.accountNumber} already bills <b>{sharedWith?.name ?? "another customer"}</b>. Bill {o.customerName} on the same account too.
          </span>
        </label>
      ) : null}
    </>
  ) : null;

  const tagsFor = (accountNumber: string): React.ReactNode[] => {
    const other = owner(accountNumber);
    if (other) {
      return [
        <Chip key="shared" kind="warn" icon="triangle-alert">
          Already bills {other.name}
        </Chip>,
      ];
    }
    if (linkedNumber === accountNumber) {
      return [
        <Chip key="linked" kind="ok" icon="link">
          Linked to this customer
        </Chip>,
      ];
    }
    return [];
  };

  const searchBox = (exclude: string[]) => {
    const hits = results ? results.list.filter((a) => !exclude.includes(a.accountNumber)).slice(0, 6) : [];
    return (
      <>
        <label className="search">
          <span className="sr">Search OneBill accounts</span>
          <Icon name="search" size={15} />
          <input
            className="input"
            data-testid="ab-search"
            placeholder="Search OneBill: name or account number"
            value={query}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="res" data-testid="ab-results">
          {!results ? null : results.error ? (
            <div className="empty">OneBill didn't answer: {results.error}</div>
          ) : hits.length === 0 ? (
            <div className="empty">No OneBill account matches “{results.query}”.</div>
          ) : (
            <div className="opts" role="radiogroup" aria-label="OneBill accounts found">
              {hits.map((a) => {
                const active = a.status === "Active";
                return (
                  <Opt
                    key={a.accountNumber}
                    testId={`ab-account-${a.accountNumber}`}
                    checked={existing?.accountNumber === a.accountNumber}
                    disabled={!active}
                    title={a.name}
                    meta={`${a.accountNumber} \u00b7 ${a.status} in OneBill`}
                    tags={active ? tagsFor(a.accountNumber) : [<Chip key="inactive" kind="stop" icon="ban">Not active, can't bill</Chip>]}
                    onPick={() => pickAccount({ accountNumber: a.accountNumber, name: a.name, status: a.status })}
                  />
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  const createOpt =
    billing && billing.state !== "linked" && from && c ? (
      <Opt
        testId="ab-create"
        checked={choice?.kind === "create"}
        disabled={!canCreate}
        title="Create a new OneBill account"
        meta={
          canCreate
            ? "Built from the Autotask record: account number, address, billing contact and email. Omni doesn't edit these; change them in Autotask and read it again."
            : `OneBill needs an address and an email, and Autotask is missing the ${from.missing.join(" and ")}.`
        }
        onPick={pickCreate}
      >
        {choice?.kind === "create" || !canCreate ? <FromAutotaskList customer={c} from={from} /> : null}
      </Opt>
    ) : null;

  const sourceTag = numberSource === "omni" || numberSource === "rpp" ? <span className={`tag ${numberSource}`}>{numberSource === "omni" ? "Omni" : "RPP"}</span> : null;
  // The number OneBill bills under: the Autotask company Number, and the RPP swap while no OneBill account is linked.
  let numberBlock: React.ReactNode = null;
  if (c && billing) {
    if (billing.state === "linked") {
      numberBlock =
        number && billing.accountNumber === number ? (
          <div className="acctno" data-testid="ab-number" data-source={numberSource ?? "none"}>
            <Icon name="hash" size={15} />
            <div className="acctno-t">
              <span>
                OneBill account number <b>{number}</b> (from Autotask)
              </span>
              {sourceTag}
              <span className="muted">Linked</span>
            </div>
          </div>
        ) : null;
    } else if (!number) {
      numberBlock = (
        <div className="acctno" data-testid="ab-number" data-source="none">
          <Icon name="hash" size={15} />
          <div className="acctno-t">
            <span>No account number in Autotask yet. Approving gives {c.name} the next PW number and writes it to Autotask before OneBill sees it.</span>
          </div>
        </div>
      );
    } else {
      numberBlock = (
        <>
          <div className="acctno" data-testid="ab-number" data-source={numberSource ?? "other"}>
            <Icon name="hash" size={15} />
            <div className="acctno-t">
              <span>
                OneBill account number <b>{number}</b> (from Autotask)
              </span>
              {sourceTag}
              {replaced && replaced !== number ? <span className="muted" data-testid="ab-number-replaced">Replaced {replaced} just now</span> : null}
              {rppOpen ? null : (
                <button className="link" type="button" data-testid="ab-rpp-open" onClick={openRpp}>
                  {numberSource === "omni" ? "Use an RPP number instead" : "Change the RPP number"}
                </button>
              )}
            </div>
          </div>
          {rppOpen ? (
            <div className="rpp-box" data-testid="ab-rpp">
              <label className="field">
                <span>RPP number</span>
                <input
                  className="input"
                  data-testid="ab-rpp-number"
                  inputMode="numeric"
                  placeholder="4 to 6 digits"
                  autoComplete="off"
                  value={rppVal}
                  onChange={(e) => {
                    setRppVal(e.target.value);
                    setRppErr(null);
                  }}
                />
              </label>
              <p className="ns-note">
                For a customer who still needs Reinvent services. Omni writes it to the Autotask company{"\u2019"}s Number{numberSource === "omni" ? `, and ${number} is set aside` : ""}. You can change it
                until the customer has a OneBill account.
              </p>
              {rppErr ? (
                <p className="err" role="alert" data-testid="ab-rpp-error">
                  {rppErr}
                </p>
              ) : null}
              <div className="btnrow">
                <button className="btn small primary" type="button" data-testid="ab-rpp-save" disabled={rppSaving} onClick={() => void saveRpp()}>
                  <Icon name="check" size={13} />
                  {rppSaving ? "Saving" : "Use this number"}
                </button>
                <button
                  className="btn small"
                  type="button"
                  data-testid="ab-rpp-keep"
                  disabled={rppSaving}
                  onClick={() => {
                    setRppOpen(false);
                    setRppErr(null);
                  }}
                >
                  Keep {number}
                </button>
              </div>
            </div>
          ) : null}
          {takenHere && choice?.kind === "create" ? (
            <div className="callout stop refuse" role="alert" data-testid="ab-refused">
              <Icon name="triangle-alert" size={15} />
              <div>
                <b>OneBill already has account {takenHere.accountNumber}. Link it instead.</b>
                <span className="sub-l">
                  {[takenHere.name, takenHere.accountNumber, takenHere.status ? `${takenHere.status} in OneBill` : null].filter(Boolean).join(" \u00b7 ")}. Omni created nothing.
                </span>
                <button
                  className="btn small white"
                  type="button"
                  data-testid="ab-link-taken"
                  onClick={() => pickAccount({ accountNumber: takenHere.accountNumber, name: takenHere.name, status: takenHere.status })}
                >
                  <Icon name="link" size={13} />
                  Link {takenHere.accountNumber}
                </button>
              </div>
            </div>
          ) : null}
        </>
      );
    }
  }

  /** The existing account ops chose when no suggestion or search result shows it (the refused number, linked instead). */
  const pickedOpt = (shown: string[]): React.ReactNode => {
    const hits = results ? results.list.slice(0, 6).map((a) => a.accountNumber) : [];
    if (!existing || shown.includes(existing.accountNumber) || hits.includes(existing.accountNumber)) return null;
    return (
      <div className="opts" role="radiogroup" aria-label="Chosen OneBill account">
        <Opt
          testId={`ab-account-${existing.accountNumber}`}
          checked
          title={existing.name ?? existing.accountNumber}
          meta={[existing.accountNumber, existing.status ? `${existing.status} in OneBill` : null].filter(Boolean).join(" \u00b7 ")}
          tags={tagsFor(existing.accountNumber)}
          onPick={() => undefined}
        />
      </div>
    );
  };

  let accountSection: React.ReactNode = <div className="empty">Reading Autotask and OneBill.</div>;
  if (c && from && billing) {
    if (billing.state === "linked") {
      const away = existing !== null && existing.accountNumber !== billing.accountNumber;
      accountSection = (
        <>
          <p className="ns-note">Linked to this customer by {LINKED_VIA[billing.via]}. Omni won't open a second account for a customer that has one.</p>
          {numberBlock}
          <div className="opts" role="radiogroup" aria-label="Linked OneBill account">
            <Opt
              testId={`ab-account-${billing.accountNumber}`}
              checked={existing?.accountNumber === billing.accountNumber}
              title={linked.data?.name ?? c.name}
              meta={linked.data ? `${billing.accountNumber} \u00b7 ${linked.data.status} in OneBill` : billing.accountNumber}
              tags={tagsFor(billing.accountNumber)}
              onPick={() => pickAccount({ accountNumber: billing.accountNumber, name: linked.data?.name ?? null, status: linked.data?.status ?? null })}
            />
          </div>
          {searchOpen || away ? (
            <>
              <div className="or">Use a different account</div>
              {searchBox([billing.accountNumber])}
            </>
          ) : (
            <p className="ns-note">
              <button className="link" type="button" data-testid="ab-other" onClick={() => setSearchOpen(true)}>
                Bill a different OneBill account
              </button>
            </p>
          )}
          {ackBoxes}
        </>
      );
    } else if (billing.state === "unreviewed") {
      const sug = billing.candidates;
      const lead = sug[0]?.why ?? null;
      const parentName = c.parentCompanyId !== null ? customers.data?.find((x) => x.autotaskCompanyId === c.parentCompanyId)?.name ?? null : null;
      accountSection = (
        <>
          <p className="ns-note" data-testid="ab-suggest-note">
            {lead === "parent"
              ? `${c.name} is a site of ${parentName ?? "its parent company"} in Autotask, so Omni suggests its parent\u2019s OneBill account. Most sites bill under their parent\u2019s account. Pick it, search, or create a new account.`
              : lead === "number"
                ? `The account number matches, but the names don\u2019t agree, so the sync didn\u2019t link ${c.name}. Pick it, search, or create a new account.`
                : sug.length
                  ? `The sync couldn't link ${c.name} to one OneBill account with certainty, so it kept ${plural(sug.length, "suggestion")}. Pick the right one, search, or create a new account.`
                  : `The sync found no OneBill account for ${c.name}. Search OneBill, or create a new account.`}
          </p>
          {numberBlock}
          {sug.length ? (
            <div className="opts" role="radiogroup" aria-label="Suggested OneBill accounts">
              {sug.map((s) => (
                <Opt
                  key={s.accountNumber}
                  testId={`ab-account-${s.accountNumber}`}
                  checked={existing?.accountNumber === s.accountNumber}
                  title={s.name}
                  meta={s.accountNumber}
                  tags={[
                    s.why === "name" ? (
                      <Chip key="why" icon="search">
                        {Math.round(s.score * 100)}% name match
                      </Chip>
                    ) : s.why === "parent" ? (
                      <Chip key="why" icon="building-2" testId={`ab-why-${s.accountNumber}`} status="parent">
                        Parent{"\u2019"}s account
                      </Chip>
                    ) : s.why === "number" ? (
                      <Chip key="why" icon="hash" testId={`ab-why-${s.accountNumber}`} status="number">
                        Same number
                      </Chip>
                    ) : (
                      <Chip key="why" icon="phone">
                        Same phone as Autotask
                      </Chip>
                    ),
                    ...tagsFor(s.accountNumber),
                  ]}
                  onPick={() => pickAccount({ accountNumber: s.accountNumber, name: s.name, status: null })}
                />
              ))}
            </div>
          ) : null}
          <div className="or">Search OneBill</div>
          {pickedOpt(sug.map((s) => s.accountNumber))}
          {searchBox(sug.map((s) => s.accountNumber))}
          <div className="or">Or</div>
          <div className="opts">{createOpt}</div>
          {ackBoxes}
        </>
      );
    } else if (blocked) {
      accountSection = (
        <>
          <Callout kind="stop" icon="triangle-alert">
            <b>OneBill can't open an account for {c.name} yet.</b>
            It needs an address and an email from Autotask, and Autotask has no{" "}
            {from.missing.map((m) => (m === "email" ? "email on any contact" : "address")).join(" and no ")}. Omni doesn't write customer details to Autotask; fix it
            there, then read Autotask again.
          </Callout>
          {numberBlock}
          <FromAutotaskList customer={c} from={from} />
          <div className="btnrow">
            <button className="btn" type="button" data-testid="ab-reread" disabled={customer.loading} onClick={() => void readAgain()}>
              <Icon name="refresh-cw" size={14} />
              {customer.loading ? "Reading Autotask" : "Read Autotask again"}
            </button>
          </div>
          {reread ? <p className="ns-note">{reread}</p> : null}
          <div className="or">Or bill an account OneBill already has</div>
          {pickedOpt([])}
          {searchBox([])}
          {ackBoxes}
        </>
      );
    } else {
      accountSection = (
        <>
          <p className="ns-note">
            {c.name} {billing.via === "ops" ? "was marked by Phoneware as having no OneBill account" : "was created in Omni and has no OneBill account yet"}.
          </p>
          {numberBlock}
          <div className="opts">{createOpt}</div>
          {reread ? <p className="ns-note">{reread}</p> : null}
          <div className="or">Or bill an account OneBill already has</div>
          {pickedOpt([])}
          {searchBox([])}
          {ackBoxes}
        </>
      );
    }
  }

  const sectionChip =
    choice?.kind === "create" && takenHere ? (
      <Chip kind="stop" icon="triangle-alert" testId="ab-account-chip" status="taken">
        Already in OneBill
      </Chip>
    ) : existing && existing.accountNumber === linkedNumber ? (
      <Chip kind="ok" icon="link">
        Linked
      </Chip>
    ) : existing ? (
      <Chip kind="ok" icon="link">
        Existing account
      </Chip>
    ) : choice?.kind === "create" ? (
      <Chip icon="plus">New account</Chip>
    ) : blocked ? (
      <Chip kind="stop" icon="lock">
        Blocked
      </Chip>
    ) : (
      <Chip kind="warn" icon="search">
        Choose one
      </Chip>
    );

  const typeChip =
    companyType === null ? null : companyType === 1 ? (
      <Chip kind="ok">Customer</Chip>
    ) : (
      <Chip kind="quiet">{companyTypeName(companyType)}</Chip>
    );
  const signedAt = q?.signature?.signedAt ?? null;
  const top = (chip: React.ReactNode, meta: string) => (
    <div className="ab-top">
      <div>
        <div className="ab-name" data-testid="ab-name">
          {o.customerName}
          {chip}
        </div>
        <span className="row-m">{meta}</span>
      </div>
      <Link className="link" to={`/orders/${encodeURIComponent(o.id)}`} onClick={onClose}>
        Open the order
      </Link>
    </div>
  );
  const problemBox = problem ? (
    <div ref={problemRef}>
      <Callout kind="stop" icon="triangle-alert">
        <span data-testid="ab-problem">{problem}</span>
      </Callout>
    </div>
  ) : null;

  if (deciding) {
    const metaLine = [o.number, dealer?.name, q ? repName(q.repEmail) : null, q?.signature ? `${q.signature.name} signed ${whenIn(q.signature.signedAt)}` : signedAt ? `signed ${whenIn(signedAt)}` : null]
      .filter(Boolean)
      .join(" \u00b7 ");
    const catWords = category ? ` in the ${category.name} category` : "";
    return (
      <Sheet
        title="Approve billing"
        label={`Approve billing for ${o.customerName}`}
        meta={o.number}
        onClose={onClose}
        wide
        foot={
          <>
            <span className={why.stop ? "why stop" : "why"} data-testid="ab-why">
              {why.text}
            </span>
            <button className="btn" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" type="button" data-testid="ab-approve" disabled={!ready || !decided || !linesOk || sending} onClick={() => void approve()}>
              <Icon name="check" size={15} />
              {sending ? "Approving" : "Approve billing"}
            </button>
          </>
        }
      >
        <div className="ab" data-testid="billing-approval" data-phase="decide">
          {top(typeChip, metaLine)}
          {problemBox}
          <dl className="facts ab-sum">
            <div>
              <dt>Monthly</dt>
              <dd>{q ? formatCents(totals.monthly) : "Reading"}</dd>
            </div>
            <div>
              <dt>One-time</dt>
              <dd>{q ? formatCents(totals.oneTime) : "Reading"}</dd>
            </div>
            <div>
              <dt>Autotask ticket</dt>
              <dd>{o.autotask.ticketNumber ?? <span className="muted">Not yet</span>}</dd>
            </div>
          </dl>
          {customer.error && !c ? (
            <div style={{ marginTop: 14 }}>
              <Callout kind="stop" icon="triangle-alert">
                Autotask didn't answer: {customer.error.message}
              </Callout>
            </div>
          ) : null}
          <section className={blocked && !existing ? "ab-sec ab-blocked" : "ab-sec"} data-testid="ab-account">
            <h3>
              <span className="ab-n">1</span>OneBill account<span className="note">{sectionChip}</span>
            </h3>
            {accountSection}
          </section>
          <section className="ab-sec" data-testid="ab-lines">
            <h3>
              <span className="ab-n">2</span>Lines to bill<span className="note">{plural(n, "line")}</span>
            </h3>
            {q ? (
              <div className="tablewrap">
                <table className="t t-bill">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="num">Qty</th>
                      <th className="num">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const needs = reqLines.find((r) => r.line === i) ?? null;
                      return (
                        <React.Fragment key={`${i}-${l.productId}`}>
                          <tr className={needs ? "ab-needs" : undefined}>
                            <td>{l.name}</td>
                            <td className="num">{l.qty}</td>
                            <td className="num">
                              {formatCents(l.unitPrice)}
                              <span className="sub">{l.billing === "monthly" ? "a month" : "once"}</span>
                            </td>
                          </tr>
                          {needs ? (
                            <tr className="ab-feat" data-testid={`ab-line-${i}-needs`}>
                              <td colSpan={3}>
                                {needs.features.map((f) => {
                                  const k = featureKey(i, f.featureName);
                                  const slug = `ab-feature-${i}-${featureSlug(f.featureName)}`;
                                  const value = valueOf(needs, f);
                                  const err = refusedValues[k] ?? (left[k] ? lineValueProblem(f.featureName, value) : null);
                                  const tel = f.featureName === TELEPHONE_NUMBER_FEATURE;
                                  // Says where the number came from while the field still holds it (#1763).
                                  const from = tel && f.prefilledFrom && f.defaultValue && telephoneDigits(value) === f.defaultValue ? f.prefilledFrom : null;
                                  return (
                                    <label className="field ab-feat-f" key={f.featureName}>
                                      <span>
                                        {f.featureName}
                                        {from ? (
                                          <span className={from === "seller" ? "tag ab-from" : "tag quiet ab-from"} data-testid={`${slug}-from`}>
                                            {PREFILLED_FROM[from]}
                                          </span>
                                        ) : null}
                                      </span>
                                      <input
                                        className={err ? "input bad" : "input"}
                                        data-testid={slug}
                                        inputMode={tel ? "tel" : undefined}
                                        autoComplete="off"
                                        placeholder={tel ? "10 digits" : undefined}
                                        aria-invalid={err ? true : undefined}
                                        value={value}
                                        onChange={(e) => {
                                          const next = e.target.value;
                                          setValues((v) => ({ ...v, [k]: next }));
                                          setRefusedValues((r) => {
                                            if (!(k in r)) return r;
                                            const rest = { ...r };
                                            delete rest[k];
                                            return rest;
                                          });
                                        }}
                                        onBlur={() => setLeft((s) => ({ ...s, [k]: true }))}
                                      />
                                      {err ? (
                                        <span className="err" role="alert" data-testid={`${slug}-error`}>
                                          {err}
                                        </span>
                                      ) : (
                                        <span className="field-note">{featureNote(f, value)}</span>
                                      )}
                                    </label>
                                  );
                                })}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>Monthly</td>
                      <td className="num">{formatCents(totals.monthly)}</td>
                    </tr>
                    <tr>
                      <td colSpan={2}>One-time</td>
                      <td className="num">{formatCents(totals.oneTime)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="empty">{quote.error ? quote.error.message : "Reading the signed quote."}</div>
            )}
            {requirements.error ? (
              <div className="ab-reqs" data-testid="ab-requirements-error">
                <Callout kind="stop" icon="triangle-alert">
                  <b>Billing waits until OneBill says what these lines need.</b>
                  {requirements.error.message}
                </Callout>
                <div className="btnrow">
                  <button className="btn" type="button" data-testid="ab-requirements-reread" disabled={requirements.loading} onClick={() => requirements.reload()}>
                    <Icon name="refresh-cw" size={14} />
                    {requirements.loading ? "Reading OneBill" : "Read OneBill again"}
                  </button>
                </div>
              </div>
            ) : q && !linesRead ? (
              <p className="ns-note">Reading what each line's OneBill plan needs.</p>
            ) : null}
            <p className="ns-note">Each line bills on the OneBill plan it's linked to in the catalog, at the signed price.</p>
          </section>
          <section className="ab-sec" data-testid="ab-company">
            <h3>
              <span className="ab-n">3</span>Customer in Autotask
            </h3>
            {companyType === null ? (
              <div className="empty">Reading Autotask.</div>
            ) : prospect ? (
              <>
                <div className="change" data-testid="ab-change">
                  <Chip kind="quiet">Prospect</Chip>
                  <Icon name="arrow-right" size={15} />
                  <Chip kind="ok" icon="check">
                    Customer
                  </Chip>
                </div>
                <p className="ns-note">
                  Omni changes the company type once OneBill has the lines, then reads Autotask back. Until you approve, {o.customerName} stays a Prospect
                  {catWords}.
                </p>
              </>
            ) : companyType === 1 ? (
              <>
                <div className="change" data-testid="ab-change">
                  <Chip kind="ok" icon="check">
                    Customer
                  </Chip>
                </div>
                <p className="ns-note">
                  {o.customerName} is already a Customer in Autotask{catWords}. Nothing changes there.
                </p>
              </>
            ) : (
              <>
                <div className="change" data-testid="ab-change">
                  <Chip kind="quiet">{companyTypeName(companyType)}</Chip>
                </div>
                <p className="ns-note">
                  Autotask lists {o.customerName} as a {companyTypeName(companyType)}. Omni changes only a Prospect to Customer, so the order holds at that step
                  until its type is fixed in Autotask.
                </p>
              </>
            )}
          </section>
          <label className="field ab-note">
            <span>Note on the approval</span>
            <textarea className="input" data-testid="ab-note" placeholder="Optional. Kept with the decision on the order." value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
      </Sheet>
    );
  }

  // ---------------------------------------------------------------- after approving: the run
  const approval = o.billingApproval;
  const company = companyOf(o);
  const wasProspect = company.fromType !== null ? company.fromType === 3 : prospect;
  const on = approval?.account?.kind === "existing" ? approval.account.accountNumber : approval?.account?.kind === "create" ? "a new OneBill account" : null;
  const account = o.onebill.accountNumber ?? (approval?.account?.kind === "existing" ? approval.account.accountNumber : null);
  const failedAt = o.onebill.state === "failed" ? "onebill" : company.state === "failed" ? "company" : o.autotask.state === "failed" ? "autotask" : null;
  const phase = o.status === "complete" ? "done" : o.status === "needs_attention" ? "failed" : o.status === "awaiting_billing_approval" ? "decide" : "running";
  const title = phase === "done" ? "Billing set up" : phase === "failed" ? "Billing failed" : phase === "decide" ? "Approve billing" : "Setting up billing";

  const obDrawn = o.onebill.state === "done" ? "done" : o.onebill.state === "failed" ? "fail" : phase === "running" ? "now" : "next";
  const coDrawn = company.state === "done" ? "done" : company.state === "failed" ? "fail" : o.onebill.state === "done" && phase === "running" ? "now" : "next";
  const obNote =
    o.onebill.state === "done"
      ? `Account ${o.onebill.accountNumber ?? ""} with ${plural(n || o.onebill.placed.length, "line")}.`
      : o.onebill.state === "failed"
        ? `Failed: ${o.onebill.error ?? "OneBill did not answer."}`
        : approval?.account?.kind === "create"
          ? `Creating the account, then placing ${plural(n, "line")}.`
          : `Placing ${plural(n, "line")} on ${on ?? "the account"}.`;
  const coNote =
    company.state === "done"
      ? wasProspect
        ? "Prospect to Customer, read back from Autotask."
        : "Already a Customer in Autotask."
      : company.state === "failed"
        ? `Failed: ${company.error ?? "Autotask did not answer."}`
        : wasProspect
          ? "Prospect to Customer, then read back."
          : "Already a Customer. Checked once OneBill has the lines.";
  const tag = (d: string) =>
    d === "done" ? (
      <Chip kind="ok" icon="check">
        Done
      </Chip>
    ) : d === "fail" ? (
      <Chip kind="stop" icon="triangle-alert">
        Failed
      </Chip>
    ) : d === "now" ? (
      <span className="chip">
        <Icon name="loader" size={10} className="spin" />
        Working
      </span>
    ) : (
      <Chip kind="quiet">Next</Chip>
    );
  const li = (drawn: string, label: string, text: string) => (
    <li className={`tl tl-${drawn}`} data-testid={`ab-run-${label}`} data-state={drawn}>
      <span className="tl-dot" aria-hidden="true">
        {drawn === "done" ? <Icon name="check" size={12} /> : null}
      </span>
      <div className="tl-main">
        <b>{label}</b>
        <span className="row-m">{text}</span>
      </div>
      {tag(drawn)}
    </li>
  );
  const by = approval?.decidedBy ? repName(approval.decidedBy) : "Phoneware";
  const statusChip =
    phase === "done" ? (
      <Chip kind="ok" icon="check">
        Complete
      </Chip>
    ) : phase === "failed" ? (
      <Chip kind="warn" icon="triangle-alert">
        Needs a retry
      </Chip>
    ) : (
      <Chip kind="quiet" icon="clock">
        Omni working
      </Chip>
    );
  const failText = failedAt === "onebill" ? o.onebill.error : failedAt === "company" ? company.error : o.autotask.error;

  return (
    <Sheet
      title={title}
      label={`${title} for ${o.customerName}`}
      meta={o.number}
      onClose={onClose}
      wide
      foot={
        phase === "failed" ? (
          <>
            <button className="btn" type="button" onClick={onClose}>
              Close
            </button>
            <button className="btn primary" type="button" data-testid="ab-retry" disabled={retrying} onClick={() => void retry()}>
              <Icon name="refresh-cw" size={15} />
              {retrying ? "Retrying" : "Retry the failed step"}
            </button>
          </>
        ) : phase === "done" ? (
          <>
            <Link className="btn" to={`/orders/${encodeURIComponent(o.id)}`} onClick={onClose}>
              Open the order
            </Link>
            <button className="btn primary" type="button" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      <div className="ab" data-testid="billing-approval" data-phase={phase}>
        {top(statusChip, [o.number, dealer?.name, q ? repName(q.repEmail) : null].filter(Boolean).join(" \u00b7 "))}
        {problemBox}
        {phase === "failed" ? (
          <Callout kind="stop" icon="triangle-alert">
            <b>{failedAt === "company" ? "Autotask didn't take the change." : failedAt === "autotask" ? "Autotask didn't take the ticket." : "OneBill didn't take the order."}</b>
            {failText ?? "The step failed."} The approval stands, and {o.customerName} is still a {wasProspect && company.state !== "done" ? "Prospect" : "Customer"} in
            Autotask.
          </Callout>
        ) : null}
        {phase === "done" ? (
          <Callout kind="ok" icon="circle-check">
            <b>Billing is set up.</b>
            OneBill account {account ?? ""} has {plural(n || o.onebill.placed.length, "line")}
            {wasProspect ? `, and ${o.customerName} is a Customer in Autotask` : ""}.
          </Callout>
        ) : null}
        <ol className="timeline ab-run">
          {li("done", "Billing approved", `${by} approved billing on ${on ?? "the linked account"}.${approval?.note ? ` "${approval.note}"` : ""}`)}
          {li(obDrawn, "Account in OneBill", obNote)}
          {li(coDrawn, "Customer in Autotask", coNote)}
        </ol>
        {phase === "running" ? <p className="ns-note">You can close this. The order keeps going and shows its progress on the order and on Ops home.</p> : null}
        {phase === "failed" ? <p className="ns-note">A retry picks up where the order stopped and never makes a second account.</p> : null}
      </div>
    </Sheet>
  );
}
